"use client";

import { View } from "@/components";
import cn from "@/utils/cn";

import type { MenuItem, DropdownProps } from "@/components/types";
import type { JSX } from "react";

/**
 * @component
 * @name Select
 * @description Renders an accessible Select menu using a native HTML select element with custom styling.
 * This approach ensures better accessibility across all devices, including mobile and screen readers.
 *
 * @example
 * ```tsx
 * const [filter, setFilter] = useState("Option 1");
 *
 * <Select
 *   items={["Option 1", "Option 2", "Option 3"]}
 *   selected={filter}
 *   setSelected={setFilter}
 *   className="w-48 bg-white dark:bg-slate-800"
 * />
 * ```
 *
 * @param {SelectProps} props - The properties for the Select component.
 * @param {string[]} props.items - The list of string items to display in the Select menu.
 * @param {string} [props.selected] - The currently selected value (for controlled mode).
 * @param {function} [props.setSelected] - Callback function called when an item is selected (for controlled mode). Receives the selected string value.
 * @param {string} [props.className] - Additional CSS classes to apply to the Select select. Defaults to full width and themed background.
 * @param {string} [props.id] - The id attribute for the select element.
 * @param {string} [props.defaultValue] - The default value for uncontrolled mode.
 * @param {string} [props.placeholder] - Text shown by the disabled placeholder option when no `selected`/`defaultValue` is set.
 * @returns {JSX.Element} The rendered Select component.
 */
const Select = ({
  className,
  id,
  items,
  defaultValue,
  selected,
  setSelected,
  children,
  placeholder,
  // `onSubmit` is only meaningful on a <form>; SelectHTMLAttributes still
  // carries it (React's generic DOM typings), and forwarding it would clash
  // with View's NextForm-flavored onSubmit(event: FormData) signature.
  onSubmit: _onSubmit,
  // Native `value` allows a readonly string[] (multi-select), which is
  // wider than ViewProps.value (string | number); `selected`/`defaultValue`
  // above already cover this component's controlled/uncontrolled value.
  value: _value,
  ...props
}: DropdownProps): JSX.Element => {
  /**
   * Handles the change event when a user selects an option
   * @param {React.ChangeEvent<HTMLSelectElement>} e - The change event
   */
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (setSelected) {
      setSelected(value);
    }
    // Propagate standard onChange if provided
    if (props.onChange) {
      props.onChange(e);
    }
  };

  // A <select> can't be controlled and uncontrolled at once — pass `value`
  // only when `selected` is actually set, otherwise fall back to
  // `defaultValue` so uncontrolled usage keeps working.
  const controlledProps =
    selected !== undefined
      ? { value: selected }
      : { defaultValue: defaultValue ?? "" };

  return (
    <View className="flex items-center gap-2">
      {children}
      <View
        id={id}
        name={id}
        type="select"
        aria-label={placeholder ?? id}
        onChange={handleChange}
        className={cn(
          "input text-start flex items-center justify-between cursor-pointer min-w-full pr-10",
          className
        )}
        {...controlledProps}
        {...props}
      >
        {/* Opción por defecto si no hay valor seleccionado */}
        {!selected && !defaultValue && (
          <View type="option" value="" disabled>
            {placeholder ?? "Seleccionar..."}
          </View>
        )}
        {items.map(({ id: itemId, label, value }: MenuItem) => (
          <View key={itemId} type="option" value={value ?? label}>
            {label}
          </View>
        ))}
      </View>
    </View>
  );
};

export default Select;
